import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Tag, Select, Button, Popconfirm, message, Empty } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getParentRequests, decideParentRequest } from '../api/parentRequests';
import useAuthStore from '../store/authStore';
import { apiErrorMessage } from '../utils/apiError';

const TYPE_LABELS = { absence: 'Devamsızlık Bildirimi', makeup: 'Telafi Hakkı Kullanma' };
const STATUS_TAGS = {
  pending: <Tag color="orange">Bekliyor</Tag>,
  approved: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
  cancelled: <Tag color="default">Geri Çekildi</Tag>,
};
// /users/:id sayfasi sadece MANAGER_ROLES icin acik (App.js) - bu sayfaya
// ogretmenler de erisebildigi icin Veli/Islem linkleri sadece yonetici
// rolleri icin tiklanabilir gosteriliyor.
const CAN_VIEW_USER_DETAIL_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

const ParentRequests = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canViewUserDetail = CAN_VIEW_USER_DETAIL_ROLES.includes(user?.role);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [decidingId, setDecidingId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getParentRequests({ status: statusFilter, per_page: 50 });
      setData(res.data.data || []);
    } catch {
      message.error('Talepler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDecide = async (id, decision) => {
    setDecidingId(id);
    try {
      await decideParentRequest(id, { decision });
      message.success(decision === 'approve' ? 'Talep onaylandı.' : 'Talep reddedildi.');
      fetchData();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setDecidingId(null);
    }
  };

  const detailFor = (r) => {
    if (r.type === 'absence') {
      const ls = r.lesson_session;
      if (!ls) return '—';
      return `${dayjs(ls.date).format('DD.MM.YYYY')} ${ls.start_time?.slice(0, 5) || ''} · ${ls.classroom?.name || ''}`;
    }
    const missed = r.makeup_right?.attendance?.lesson_session;
    const requested = r.lesson_session;
    return (
      <span>
        {missed && (
          <div className="text-gray-400">
            Kaçırılan: {dayjs(missed.date).format('DD.MM.YYYY')} {missed.start_time?.slice(0, 5) || ''}
          </div>
        )}
        {requested ? (
          <div>
            Talep edilen: {dayjs(requested.date).format('DD.MM.YYYY')} {requested.start_time?.slice(0, 5) || ''} · {requested.classroom?.name || ''}
            {r.requested_session_occupancy && (
              <Tag className="ml-1" color={r.requested_session_occupancy.count < r.requested_session_occupancy.capacity ? 'green' : 'red'}>
                {r.requested_session_occupancy.count}/{r.requested_session_occupancy.capacity}
              </Tag>
            )}
          </div>
        ) : '—'}
      </span>
    );
  };

  const columns = [
    {
      title: 'Veli',
      render: (_, r) => {
        if (!r.parent_user) return '—';
        const label = `${r.parent_user.name} ${r.parent_user.surname}`;
        return canViewUserDetail ? (
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.parent_user.id}`)}>
            {label}
          </Button>
        ) : label;
      },
    },
    {
      title: 'Öğrenci',
      render: (_, r) => r.student ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/students/${r.student.id}`)}>
          {r.student.name} {r.student.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'Tür',
      render: (_, r) => <Tag color={r.type === 'absence' ? 'volcano' : 'geekblue'}>{TYPE_LABELS[r.type]}</Tag>,
    },
    { title: 'Detay', render: (_, r) => <span className="text-sm">{detailFor(r)}</span> },
    { title: 'Not', dataIndex: 'note', render: (v) => v || <span className="text-gray-400">—</span> },
    { title: 'Tarih', dataIndex: 'created_at', render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm') },
    { title: 'Durum', dataIndex: 'status', render: (v) => STATUS_TAGS[v] || v },
    {
      title: 'İşlem',
      render: (_, r) => r.status !== 'pending' ? (
        r.decided_by ? (
          canViewUserDetail ? (
            <Button type="link" style={{ padding: 0, height: 'auto' }} className="text-sm" onClick={() => navigate(`/users/${r.decided_by.id}`)}>
              {r.decided_by.name} {r.decided_by.surname}
            </Button>
          ) : (
            <span className="text-gray-400 text-sm">{r.decided_by.name} {r.decided_by.surname}</span>
          )
        ) : null
      ) : (
        <div className="flex gap-2">
          <Popconfirm
            title="Talebi onaylamak istediğinize emin misiniz?"
            description={r.type === 'absence'
              ? 'Onaylarsanız öğrenci bu ders için "Gelmeyecek" olarak işaretlenecek ve buna göre borç/telafi hakkı otomatik oluşacak.'
              : `Onaylarsanız telafi otomatik olarak velinin talep ettiği güne planlanacak (öğrenci detayına gitmenize gerek yok).${r.requested_session_occupancy && r.requested_session_occupancy.count >= r.requested_session_occupancy.capacity ? ' Dikkat: bu ders artık dolu görünüyor!' : ''}`}
            onConfirm={() => handleDecide(r.id, 'approve')}
            okText="Evet, Onayla"
            cancelText="Vazgeç"
          >
            <Button size="small" type="primary" icon={<CheckOutlined />} loading={decidingId === r.id}>Onayla</Button>
          </Popconfirm>
          <Popconfirm
            title="Talebi reddetmek istediğinize emin misiniz?"
            description="Veli, talebinin reddedildiği konusunda bilgilendirilecek."
            onConfirm={() => handleDecide(r.id, 'reject')}
            okText="Evet, Reddet"
            cancelText="Vazgeç"
          >
            <Button size="small" danger icon={<CloseOutlined />} loading={decidingId === r.id}>Reddet</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold mb-0">Veli Talepleri</h2>
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 180 }}
          options={[
            { value: 'pending', label: 'Bekleyen' },
            { value: 'approved', label: 'Onaylanan' },
            { value: 'rejected', label: 'Reddedilen' },
            { value: 'all', label: 'Tümü' },
          ]}
        />
      </div>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          locale={{ emptyText: <Empty description="Talep yok." /> }}
          pagination={false}
        />
      </Card>
    </div>
  );
};

export default ParentRequests;
